'use client';

import React from 'react';

export interface SegmentedOption {
  value: string;
  label: React.ReactNode;
}

export interface SegmentedToggleProps {
  options: SegmentedOption[];
  value: string;
  onChange?: (value: string) => void;
  size?: 'sm' | 'md';
  style?: React.CSSProperties;
}

export function SegmentedToggle({
  options = [],
  value,
  onChange,
  size = 'md',
  style = {},
}: SegmentedToggleProps) {
  const h = size === 'sm' ? 32 : 40;
  const idx = Math.max(
    0,
    options.findIndex((o) => o.value === value),
  );
  const pct = options.length ? 100 / options.length : 100;

  return (
    <div
      style={{
        position: 'relative',
        display: 'inline-flex',
        padding: 3,
        height: h,
        background: 'var(--bg-sunken)',
        borderRadius: 'var(--r-full)',
        ...style,
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 3,
          bottom: 3,
          insetInlineStart: `calc(${idx * pct}% + 3px)`,
          width: `calc(${pct}% - 6px)`,
          background: 'var(--bg-surface)',
          borderRadius: 'var(--r-full)',
          boxShadow: 'var(--shadow-sm)',
          transition: 'inset-inline-start var(--dur-standard) var(--ease-decel)',
        }}
      />
      {options.map((o) => {
        const active = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            onClick={() => onChange && onChange(o.value)}
            style={{
              position: 'relative',
              zIndex: 1,
              flex: 1,
              minWidth: 64,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              fontFamily: 'var(--font-sans)',
              fontSize: 'var(--fs-body)',
              fontWeight: (active
                ? 'var(--fw-semibold)'
                : 'var(--fw-medium)') as React.CSSProperties['fontWeight'],
              color: active ? 'var(--text-primary)' : 'var(--text-secondary)',
              transition: 'color var(--dur-standard)',
              paddingInline: 'var(--sp-4)',
              whiteSpace: 'nowrap',
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
